import axiosInstance from './AxiosService';
import ErrorBoundary from '../components/common/ErrorBoundery';
import axios from 'axios';
import Cookies from 'js-cookie';

const appService = {
    fetchApps: async () => {
        try {
            console.log('Application Service: Fetching applications...');
            
            // First try with the regular axiosInstance that has interceptors
            const response = await axiosInstance.get('applications/');
            console.log('Applications request completed using axiosInstance');
            return response.data;
        } catch (error) {
            console.error('Error fetching applications with axiosInstance:', error);
            
            // If that fails, try with a direct call with the token from multiple sources
            try {
                let token = localStorage.getItem('token');
                if (!token) {
                    token = Cookies.get('token');
                }
                
                if (!token) {
                    console.error('No authentication token available for fetching applications');
                    throw new Error('Authentication token not found');
                }
                
                // Create a one-time axios instance with the token
                const directResponse = await axios.get(`${axiosInstance.defaults.baseURL}applications/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                console.log('Applications request completed using direct axios with token');
                return directResponse.data;
            } catch (directError) {
                console.error('Error fetching applications with direct axios:', directError);
                throw directError;
            }
        }
    },

    fetchAppsById: async (appId) => {
        try {
            console.log(`Fetching application with ID: ${appId}`);
            const response = await axiosInstance.get(`applications/${appId}/`);
            console.log(`Successfully fetched application: ${appId}`, response.data);
            return response.data;
        } catch (error) {
            console.error(`Error fetching application with ID ${appId}:`, error);
            console.error(`Error details:`, error.response?.data || 'No response data');
            console.error(`Error status:`, error.response?.status || 'No status code');
            // Return null instead of undefined to make checks more explicit
            return null;
        }
    },

    fetchAppsByOwner: async (ownerId) => {
        try {
            const response = await axiosInstance.get(`applications/myapp/${ownerId}/`);
            return response.data;
        } catch (error) {
            console.log(`Error fetching applications for owner ${ownerId}: ${error.message}`);
        }
    },

    fetchAppsByCompany: async (COMPANY_ID) => {
        try {
            const response = await axiosInstance.get(`applications/myapp/company/${COMPANY_ID}/`);
            return response.data;
        } catch (error) {
            console.log(`Error fetching applications for owner ${COMPANY_ID}: ${error.message}`);
        }
    },

    fetchAppsByType: async (appType) => {
        try {
            const response = await axiosInstance.get(`applications/myapp/apptype/${appType}/`);
            return response.data;
        } catch (error) {
            console.log(`Error fetching applications of type ${appType}: ${error.message}`);
        }
    },

    createApp: async (appData) => {
        try {
            const response = await axiosInstance.post(`applications/`, appData);
            return response.data;
        } catch (error) {
            console.log(`Error creating application: ${error.message}`);
        }
    },

    updateApp: async (appId, appData) => {
        try {
            const response = await axiosInstance.put(`applications/${appId}/`, appData);
            return response.data;
        } catch (error) {
            console.log(`Error updating application with ID ${appId}: ${error.message}`);
        }
    },

    deleteApp: async (appId) => {
        try {
            const response = await axiosInstance.delete(`applications/${appId}/`);
            return response.data;
        } catch (error) {
            console.log(`Error deleting application with ID ${appId}: ${error.message}`);
        }
    },

    fetchAppsRecordById: async (APP_NAME) => {
        const response = await axiosInstance.get(`app-users/${APP_NAME}/`);
        return response.data;
    },

    fetchAppsRecordByIdAndGrantDate: async (APP_NAME, startDate, endDate) => {
        try {
            let url = `app-users/date-granted/${APP_NAME}/`;
            
            // Add date range parameters if provided
            if (startDate && endDate) {
                url += `?start_date=${startDate}&end_date=${endDate}`;
            }
            
            const response = await axiosInstance.get(url);
            console.log(`Successfully fetched date-granted data for app ${APP_NAME}:`, response.data);
            return response.data;
        } catch (error) {
            console.error(`Error fetching date-granted data for app ${APP_NAME}:`, error);
            console.error(`Error details:`, error.response?.data || 'No response data');
            console.error(`Error status:`, error.response?.status || 'No status code');
            throw error;
        }
    },

    fetchAppsRecordByIdAndRemoveDate: async (APP_NAME, startDate, endDate) => {
        try {
            let url = `app-users/date-removed/${APP_NAME}/`;
            
            // Add date range parameters if provided
            if (startDate && endDate) {
                url += `?start_date=${startDate}&end_date=${endDate}`;
            }
            
            const response = await axiosInstance.get(url);
            console.log(`Successfully fetched date-removed data for app ${APP_NAME}:`, response.data);
            return response.data;
        } catch (error) {
            console.error(`Error fetching date-removed data for app ${APP_NAME}:`, error);
            console.error(`Error details:`, error.response?.data || 'No response data');
            console.error(`Error status:`, error.response?.status || 'No status code');
            throw error;
        }
    },

    //PROVISIONING
    getProvisioningProcessByID: async(APP_NAME) => {
        const response = await axiosInstance.get(`process/provisioning/${APP_NAME}/`);
        return response.data;
    }, 

    createProvisioningProcess: async(appData) => {
        const response = await axiosInstance.post(`process/provisioning/`, appData);
        return response.data;
    }, 

    updateProvisioningProcess: async(APP_NAME, appData) => {
        const response = await axiosInstance.put(`process/provisioning/${APP_NAME}/`, appData);
        return response.data;
    }, 

    //TERMINATION
    getTerminationProcessByID: async(APP_NAME) => {
        const response = await axiosInstance.get(`process/termination/${APP_NAME}/`);
        return response.data;
    }, 

    createTerminationProcess: async(appData) => {
        const response = await axiosInstance.post(`process/termination/`, appData);
        return response.data;
    }, 

    updateTerminationProcess: async(APP_NAME, appData) => {
        const response = await axiosInstance.put(`process/termination/${APP_NAME}/`, appData);
        return response.data;
    }, 

    //UAR

    getUARProcessByID: async(APP_NAME) => {
        try {
            console.log(`Fetching UAR process for app ID: ${APP_NAME}`);
            const response = await axiosInstance.get(`process/uar/${APP_NAME}/`);
            console.log('UAR process fetch response:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Error fetching UAR process for app ID ${APP_NAME}:`, error.response || error);
            // Return null instead of throwing to allow component to handle this gracefully
            return null;
        }
    }, 

    createUARProcess: async(appData) => {
        try {
            console.log('Creating UAR process with data:', appData);
            const response = await axiosInstance.post(`process/uar/`, appData);
            console.log('UAR process create response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error creating UAR process:', error.response || error);
            throw error; // Rethrow to allow component to handle
        }
    }, 

    updateUARProcess: async(APP_NAME, appData) => {
        try {
            console.log(`Updating UAR process for app ID ${APP_NAME} with data:`, appData);
            const response = await axiosInstance.put(`process/uar/${APP_NAME}/`, appData);
            console.log('UAR process update response:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Error updating UAR process for app ID ${APP_NAME}:`, error.response || error);
            throw error; // Rethrow to allow component to handle
        }
    }, 

    
     //ADMIN

    getAdminProcessByID: async(APP_NAME) => {
        const response = await axiosInstance.get(`process/admin/${APP_NAME}/`);
        return response.data;
    }, 

    createAdminProcess: async(appData) => {
        const response = await axiosInstance.post(`process/admin/`, appData);
        return response.data;
    }, 

    updateAdminProcess: async(APP_NAME, appData) => {
        const response = await axiosInstance.put(`process/admin/${APP_NAME}/`, appData);
        return response.data;
    }, 

    // Bulk upload applications
    // This function expects an API endpoint at '/applications/bulk/' that accepts
    // an array of application objects in the request body.
    // Each application object should contain the required fields: COMPANY_ID, APP_NAME, APP_TYPE
    // And optional fields: APP_DESCRIPTION, SOX_INSCOPE, APPLICATION_OWNER
    bulkCreateApps: async (appsData) => {
        try {
            const response = await axiosInstance.post(`applications/bulk/`, appsData);
            return response.data;
        } catch (error) {
            console.error(`Error bulk creating applications: ${error.message}`);
            throw error; // Re-throw to handle in the component
        }
    },

    uploadBulkUsers: async function(appId, users) {
        try {
            const response = await axiosInstance.post(`/apps/${appId}/bulk-user-upload/`, { users });
            
            // If upload was successful, log the job
            if (response.data && response.data.success) {
                await this.logUserUploadJob(appId, response.data);
            }
            
            return response.data;
        } catch (error) {
            console.error('Error in uploadBulkUsers:', error);
            throw error;
        }
    },

    // Add a new method to log the job
    logUserUploadJob: async function(appId, uploadResults) {
        try {
            // Calculate total processed records (including skipped)
            const totalProcessed = 
                (uploadResults.created || 0) + 
                (uploadResults.updated || 0) + 
                (uploadResults.skipped || 0);
            
            // Include skipped records in the statistics
            const jobData = {
                APP_NAME: appId,
                JOB_NAME: "User Bulk Upload",
                JOB_FILE_NAME: "Manual Upload",
                JOB_FILE_DESTINATION: "Internal",
                JOB_DATE: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
                JOB_COMPLETE: true,
                SOURCE_LINE_COUNT: uploadResults.received_data_count || "0",
                CREATED_COUNT: `${uploadResults.created || 0} created, ${uploadResults.updated || 0} updated, ${uploadResults.skipped || 0} skipped`,
                UPLOADED_BY: "User",
                JOB_ERROR: uploadResults.skipped ? 
                    `${uploadResults.skipped} records skipped (no changes needed)` : 
                    (totalProcessed === 0 ? "No records were processed" : "")
            };
            
            const response = await axiosInstance.post(`/apps/${appId}/log-user-job/`, jobData);
            return response.data;
        } catch (error) {
            console.error('Error logging upload job:', error);
            // We don't throw here so it won't affect the main upload success flow
        }
    },

    // SOD Ruleset Management
    fetchSODRules: async () => {
        try {
            const response = await axiosInstance.get('sod-rules/');
            return response.data;
        } catch (error) {
            console.error('Error fetching SOD rules:', error);
            throw error;
        }
    },
    
    fetchSODRulesByApp: async (appId) => {
        try {
            const response = await axiosInstance.get(`sod-rules/app/${appId}/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching SOD rules for app ${appId}:`, error);
            throw error;
        }
    },
    
    createSODRule: async (ruleData) => {
        try {
            const response = await axiosInstance.post('sod-rules/', ruleData);
            return response.data;
        } catch (error) {
            console.error('Error creating SOD rule:', error);
            throw error;
        }
    },
    
    updateSODRule: async (ruleId, ruleData) => {
        try {
            const response = await axiosInstance.put(`sod-rules/${ruleId}/`, ruleData);
            return response.data;
        } catch (error) {
            console.error(`Error updating SOD rule ${ruleId}:`, error);
            throw error;
        }
    },
    
    deleteSODRule: async (ruleId) => {
        try {
            const response = await axiosInstance.delete(`sod-rules/${ruleId}/`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting SOD rule ${ruleId}:`, error);
            throw error;
        }
    },
    
    analyzeSODRules: async (appId) => {
        try {
            const response = await axiosInstance.post('audit/analyze-sod-rules/', { app_id: appId });
            return response.data;
        } catch (error) {
            console.error(`Error analyzing SOD rules for app ${appId}:`, error);
            throw error;
        }
    }
};

export default appService;
